<template>
    <asli-form-section title="Terms and Conditions">
        <div id="documentScrollAnchor"></div>
        <div class="mb-8">
            <asli-checkbox
                v-model="confirmations.termsOfBusiness"
                name="agreed-to-terms-and-conditions"
                id="agreed-to-terms-and-conditions"
                no-border
                is-descriptive
                is-custom-label
                rules="required-checkbox:anpost_terms_of_business"
            >
                <template #customLabel>
                    <div class="font-bold text-lg">
                        I confirm I have read and accept the
                        <asli-link :href="siteLinks.termsOfBusiness" class="hover:underline !font-bold"
                            >An Post Insurance Terms of Business</asli-link
                        >
                    </div>
                </template>
            </asli-checkbox>
        </div>
        <div class="mb-8">
            <asli-checkbox
                v-model="confirmations.supplierPrivacy"
                name="agreed-to-supplier-privacy"
                id="agreed-to-supplier-privacy"
                no-border
                is-descriptive
                is-custom-label
                rules="required-checkbox:anpost_supplier_privacy_notice"
            >
                <template #customLabel>
                    <div class="font-bold text-lg">
                        I have read and agree to the
                        <asli-link :href="supplierInformationDocumentLink" class="hover:underline !font-bold">
                            An Post Insurance Important Supplier Information</asli-link
                        >
                        and
                        <asli-link :href="siteLinks.privacyPolicy" class="hover:underline !font-bold"
                            >Privacy Notice</asli-link
                        >
                    </div>
                </template>
            </asli-checkbox>
        </div>
        <asli-checkbox
            v-model="confirmations.statementOfFact"
            name="statement-of-fact"
            id="statement-of-fact"
            no-border
            is-descriptive
            is-custom-label
            rules="required-checkbox:anpost_statement_of_fact"
        >
            <template #customLabel>
                <div class="font-bold text-lg">
                    I confirm that I have read the Home Insurance
                    <asli-link
                        v-if="isQuoteDocumentsSupported && statementOfFactDocument && !isDocumentLoading"
                        :href="statmentOfFactLink"
                        class="hover:underline !font-bold"
                        target="_blank"
                        @click="handleDocumentClick"
                    >
                        Statement of Fact
                    </asli-link>
                    <span v-else-if="isDocumentLoading" class="relative inline-flex items-center">
                        <span class="text-grey-200 !font-bold">Statement of Fact</span>
                        <loading-spinner />
                    </span>
                    <span v-else class="!font-bold"> Statement of Fact </span>
                    and the information is correct.
                    <span class="text-sm text-gray-700">
                        (Please note, you will need to allow pop ups on this site to view this document. The full
                        quotation document including the Statement of Fact is also currently being emailed to you.)
                    </span>
                </div>
            </template>
        </asli-checkbox>
    </asli-form-section>
</template>

<script lang="ts" setup>
import { getSupplierInformationDocumentLink, siteLinks } from "@/utils/links";
import { useProposalStore } from "@/stores/risk/risk";
import { AsliFormSection, AsliLink, AsliCheckbox } from "@mbs-npm/anpost-ui";
import { computed, reactive, ref, onMounted, watch } from "vue";
import { QuoteDocumentsTypeKey } from "../YourQuotes/types";
import LoadingSpinner from "@/components/LoadingSpinner.vue";

const proposalStore = useProposalStore();
const proposal = computed(() => proposalStore.proposal);
const uuid = computed(() => proposal.value.uuid);

const supplierInformationDocumentLink = computed(() => getSupplierInformationDocumentLink(proposal.value.quote));
const statementOfFactDocument = computed(
    () =>
        proposal.value.quote?.quote_documents?.find(doc => doc.type === QuoteDocumentsTypeKey.STATEMENT_OF_FACTS) ||
        null
);
const statmentOfFactLink = computed(() =>
    statementOfFactDocument.value ? `/document/${statementOfFactDocument.value.id}` : "#"
);

const isDocumentLoading = ref(false);
const documentCheckAttempts = ref(0);
const maxCheckAttempts = 10;
const isQuoteDocumentsSupported = ref<boolean>(true);

const confirmations = reactive<{
    termsOfBusiness: boolean | null;
    supplierPrivacy: boolean | null;
    statementOfFact: boolean | null;
}>({
    termsOfBusiness: null,
    supplierPrivacy: null,
    statementOfFact: null
});

async function checkForQuoteDocument() {
    if (!isQuoteDocumentsSupported.value || !uuid.value) {
        isDocumentLoading.value = false;
        return;
    }

    try {
        await proposalStore.getQuoteDocuments(uuid.value);

        if (documentCheckAttempts.value >= maxCheckAttempts) {
            isQuoteDocumentsSupported.value = false;
            isDocumentLoading.value = false;
            return;
        }

        if (
            proposal.value?.quote?.quote_documents &&
            Array.isArray(proposal.value.quote.quote_documents) &&
            proposal.value.quote.quote_documents.length > 0
        ) {
            if (!statementOfFactDocument.value) {
                documentCheckAttempts.value++;
                setTimeout(checkForQuoteDocument, 5000);
            } else {
                isDocumentLoading.value = false;
            }
        } else {
            documentCheckAttempts.value++;
            setTimeout(checkForQuoteDocument, 5000);
        }
    } catch (err) {
        if (documentCheckAttempts.value >= maxCheckAttempts) {
            isQuoteDocumentsSupported.value = false;
            isDocumentLoading.value = false;
            return;
        }
        documentCheckAttempts.value++;
        setTimeout(checkForQuoteDocument, 5000);
    }
}

async function handleDocumentClick(event: MouseEvent) {
    if (!statementOfFactDocument.value) {
        event.preventDefault();
        if (!isDocumentLoading.value) {
            isDocumentLoading.value = true;
            await checkForQuoteDocument();
        }
    }
}

onMounted(() => {
    if (uuid.value) proposalStore.generateQuoteDocuments(uuid.value);
    if (proposal.value.quote && !statementOfFactDocument.value) {
        isDocumentLoading.value = true;
        checkForQuoteDocument();
    }
});

function scrollToCheckboxes() {
    document.getElementById("documentScrollAnchor")?.scrollIntoView({
        behavior: "smooth"
    });
}

defineExpose({
    scrollToCheckboxes
});
</script>
